import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    getContrastRatio,
    lighten,
    Stack,
    type SxProps,
    TextField,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import Chip from '/@/components/bayer/chip';
import MapComponent from '/@/components/map';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useMyFields } from '/@/hooks/fields';
import { useMaps } from '/@/hooks/maps';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { parseMaps } from '/@/store/maps/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import { convertCountryBoundsToGoogleLatLng } from '/@/views/fields/field-new-v2/utils';
import Dots, { References, TextMetricValue } from '../../../components/dots';

const MapVersionSelector = ({
    selectedMapVersion,

    sx,
}: {
    selectedMapVersion: any;
    sx: SxProps;
}) => (
    <Stack spacing={1} p={2} sx={{ background: 'white', border: (t) => `1px solid ${t.palette.divider}`, ...sx }}>
        <Typography fontWeight='bold' color='primary'>
            {_('Referencias')}
        </Typography>

        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {selectedMapVersion.mapValues.map((mv) => (
                <Chip
                    sx={{
                        background: `#${mv.color}`,
                        '& .MuiChip-label': (t) => ({
                            ...t.typography.caption,
                            fontWeight: 700,
                            color:
                                getContrastRatio('#000000', `#${mv.color}`) > 4.5
                                    ? '#000000 !important'
                                    : '#ffffff !important',
                        }),
                        '&:hover': {
                            background: lighten(`#${mv.color}`, 0.3),
                        },
                    }}
                    label={mv.value}
                    key={mv.id}
                />
            ))}
        </Stack>
        <Stack direction='row' spacing={1}>
            <Typography
                variant='caption'
                color='primary.mui.inactive'
            >{`${_('Unidad:')} ${_(selectedMapVersion.referenceUnit)}`}</Typography>
            <Typography
                variant='caption'
                color='primary.mui.inactive'
            >{`${_('Fuente:')} ${_(selectedMapVersion.source)}`}</Typography>
        </Stack>
    </Stack>
);

interface LocationOption {
    location: string;
    id: string;
    lat: number;
    lng: number;
}

const MapSection = ({ product }: { product: any }) => {
    const mapRef = useRef<null | MapComponent>(null);

    const [inputValue, setInputValue] = useState<string>('');
    const [selectedLocation, setSelectedLocation] = useState<LocationOption | undefined>(undefined);
    const [open, setOpen] = useState(false);
    const [selectedMap, setSelectedMap] = useState<any>();
    const [selectedMapVersion, setSelectedMapVersion] = useState<any>();
    const [locations, setLocations] = useState<LocationOption[]>([]);
    const [locationsLoading, setLocationsLoading] = useState(false);

    const country = useSelector(getSessionCountry);
    const fieldsQuery = useMyFields({
        pageSize: 10000,
        page: 1,
        selectOnly: 'id,lat,lng,name,location,altitude,area,geometry,externalProvider,node',
        country: country.code,
    });
    const allFields = useMemo(() => {
        return fieldsQuery.data?.results || [];
    }, [fieldsQuery.data]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (allFields.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            allFields.map(({ lat, lng }) => {
                bounds.extend(new google.maps.LatLng(lat, lng));
            });
            map.fitBounds(bounds);
            setTimeout(
                ({ map }) => {
                    if (!map) return;
                    if (map.getZoom() > 16) map.setZoom(16);
                },
                100,
                { map: map }
            );
        } else if (country) {
            const googleBounds = convertCountryBoundsToGoogleLatLng(country.bounds);
            map.fitBounds(googleBounds);
        }
    }, [allFields, country]);

    const mapsQuery = useMaps({ country: country.code });
    const filteredMaps = useMemo(() => {
        const maps = mapsQuery.data || [];
        const metricIdSet = new Set((product.metrics || []).map((m) => m.id));
        const filteredMapVersions = maps.filter((m) => metricIdSet.has(m.cornMetric?.id));
        const formattedMaps = parseMaps(filteredMapVersions).filter((map) => map.versions.length > 0);

        return formattedMaps;
    }, [mapsQuery.data, product.metrics]);

    useEffect(() => {
        const firstMap = filteredMaps?.[0];
        if (firstMap) {
            setSelectedMap(firstMap);
            setSelectedMapVersion(firstMap?.versions?.[0]);
        }
    }, [filteredMaps]);
    const debouncedSearch = useDebounce(inputValue, 300);

    useEffect(() => {
        if (!debouncedSearch || debouncedSearch.length <= 1) {
            setLocations([]);
            setLocationsLoading(false);
            return;
        }

        if (typeof google === 'undefined' || !google.maps) {
            setLocations([]);
            setLocationsLoading(false);
            return;
        }

        let active = true;
        setLocationsLoading(true);

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                // Force suggestions to country code
                address: debouncedSearch,
                region: country?.code,
                componentRestrictions: { country: country?.code },
            },
            (results, status) => {
                if (!active) return;

                if (status === 'OK' && results) {
                    const mapped = results
                        .slice(0, 5)
                        .filter((r) => !!r.geometry && !!r.geometry.location)
                        .map((r, index) => ({
                            location: r.formatted_address,
                            id: r.place_id || `${index}`,
                            lat: r.geometry!.location!.lat(),
                            lng: r.geometry!.location!.lng(),
                        }));
                    setLocations(mapped);
                } else {
                    setLocations([]);
                }

                setLocationsLoading(false);
            }
        );

        return () => {
            active = false;
        };
    }, [country?.code, debouncedSearch]);

    return (
        <Stack
            p={2}
            sx={{
                background: '#F5F5F5',
            }}
            spacing={2}
        >
            <Stack spacing={0.5}>
                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                    {_('Mapa de presión de enfermedades y caracteres agronómicos')}
                </Typography>
                <Typography variant='body2' color='primary.mui.inactive'>
                    {_('Compará el perfil defensivo del híbrido en relación con los niveles de riesgo del lote ')}
                </Typography>
            </Stack>

            <Grid2 container spacing={2}>
                <Grid2 xs={12} md={4}>
                    <Autocomplete
                        value={selectedLocation}
                        inputValue={inputValue}
                        open={open}
                        onOpen={() => {
                            setOpen(true);
                            setInputValue('');
                        }}
                        onClose={() => {
                            setOpen(false);
                            if (selectedLocation) {
                                setInputValue(selectedLocation.location);
                            }
                        }}
                        options={locations}
                        onChange={(_, option) => {
                            if (!option) {
                                return;
                            }
                            setSelectedLocation(option);
                            setInputValue(option.location);
                        }}
                        onInputChange={(_event, newInputValue, reason) => {
                            if (reason !== 'reset') {
                                setInputValue(newInputValue);
                            }
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.location ?? ''}
                        noOptionsText={
                            !inputValue?.trim()
                                ? _('Escribí el nombre de una localidad')
                                : _('No hay localidades que coincidan con la búsqueda')
                        }
                        loading={locationsLoading}
                        loadingText={_('Cargando')}
                        filterOptions={(x) => x}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={_('Localidades')}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {locationsLoading ? <CircularProgress color='inherit' size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        disableClearable
                    />
                </Grid2>
                {selectedMap && (
                    <>
                        <Grid2 xs={12} md={4}>
                            <MuiSelect
                                inputLabel={_('Filtrar por problematica')}
                                options={filteredMaps.map((m) => ({
                                    label: _(m.name),
                                    value: m.id,
                                }))}
                                value={selectedMap.id}
                                formControlProps={{
                                    sx: {
                                        width: '100%',
                                    },
                                }}
                                sx={{
                                    background: 'white',
                                }}
                                onChange={(e) => {
                                    const selectedMap = filteredMaps.find((m) => m.id === e.target.value);
                                    setSelectedMap(selectedMap);
                                    setSelectedMapVersion(selectedMap?.versions?.[0]);
                                }}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={4}>
                            <MuiSelect
                                inputLabel={_('Siembra')}
                                options={selectedMap.versions.map((m) => ({
                                    label: _(m.versionLabel),
                                    value: m.id,
                                }))}
                                value={selectedMapVersion.id}
                                formControlProps={{
                                    sx: {
                                        width: '100%',
                                        mt: '0 !important',
                                    },
                                }}
                                onChange={(o) => {
                                    setSelectedMapVersion(selectedMap.versions.find((m) => m.id === o.target.value));
                                }}
                                sx={{
                                    background: 'white',
                                }}
                            />
                        </Grid2>
                    </>
                )}
            </Grid2>
            <Box width='100%' height='500px' position='relative'>
                <MapComponent
                    ref={mapRef}
                    fields={allFields}
                    clusterMarkers
                    country={country.code}
                    noGeocoder
                    location={selectedLocation?.location}
                    overlay={{ overlay: selectedMap, version: selectedMapVersion }}
                    showOverlayCard={false}
                    simplifyPolygons
                >
                    {selectedMap && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                            }}
                        >
                            <MapVersionSelector
                                selectedMapVersion={selectedMapVersion}
                                sx={{
                                    m: 1,
                                    maxWidth: '350px',
                                }}
                            />
                        </Box>
                    )}
                </MapComponent>
            </Box>
        </Stack>
    );
};

export const MetricsMiniCard = ({ product, onClick }: { product: any; onClick: () => void }) => {
    if (product.metrics?.length === 0) return null;
    return (
        <Stack spacing={2} p={2} sx={{ border: (t) => `1px solid ${t.palette.divider}` }}>
            <Stack spacing={1}>
                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                    {_('Perfil Defensivo ')}
                </Typography>
                <Typography variant='body2' color='primary.mui.inactive'>
                    {_('Puntos destacados de su comportamiento sanitario.')}
                </Typography>
            </Stack>
            <Stack spacing={1} divider={<Divider />}>
                {(product?.metrics?.length || 0) > 0 &&
                    product.metrics.slice(0, 2).map((metric) => (
                        <Stack key={metric.id} direction='row' justifyContent='space-between'>
                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                {_(metric.name)}
                            </Typography>
                            <TextMetricValue value={Number(metric.value)} />
                        </Stack>
                    ))}
            </Stack>
            <Typography
                variant='body1'
                color='secondary'
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={onClick}
            >
                {_('Ver perfil completo')}
            </Typography>
        </Stack>
    );
};

const ProductExplorationMetrics = ({ product }: { product: any }) => {
    const { type } = useParams();
    const navigate = useNavigate();

    return (
        <Stack spacing={4}>
            <Stack spacing={2}>
                <Typography variant='h5' color='primary'>
                    {_('Comportamiento del híbrido frente a enfermedades e incidencias')}
                </Typography>
                <Typography variant='body1' color='primary.mui.inactive'>
                    {_('Con esta información podes evaluar si es adecuado según el riesgo del lote del productor.')}
                </Typography>
            </Stack>
            <Grid2 container spacing={3} py={2}>
                <Grid2 xs={12} sm={6}>
                    <Stack spacing={1} height='100%'>
                        {product.metrics.map((metric: any) => (
                            <Stack
                                key={metric.id}
                                p={2}
                                justifyContent='space-between'
                                direction='row'
                                sx={{
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                }}
                                flexWrap='wrap'
                                useFlexGap
                            >
                                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                    {_(metric.name)}
                                </Typography>
                                <Dots
                                    numberOfDots={9}
                                    selected={Number(metric.value)}
                                    sx={{
                                        width: 'auto',
                                    }}
                                />
                            </Stack>
                        ))}
                        <References
                            numberOfDots={9}
                            sx={{
                                p: 2,
                                background: '#F5F5F5',
                                border: (t) => `1px solid ${t.palette.divider}`,
                                width: 'auto',
                                maxWidth: 'fit-content',
                            }}
                        />
                        <Button
                            variant='outlined'
                            color='primary'
                            sx={{
                                mt: { xs: 'auto' },
                                width: 'fit-content',
                            }}
                            onClick={() => {
                                navigate(
                                    reverse('productExploration.comparator', {
                                        params: { catalogType: type, productIds: String(product.id) },
                                    })
                                );
                            }}
                        >
                            {_('Comparar con otros productos')}
                        </Button>
                    </Stack>
                </Grid2>
                <Grid2 xs>
                    <MapSection product={product} />
                </Grid2>
            </Grid2>
        </Stack>
    );
};

export default ProductExplorationMetrics;
